/// Rendering only. Every figure here already came from the API.

export const miles = (value: number, places = 2) => `${value.toFixed(places)} mi`;

export const steps = (value: number) => Math.round(value).toLocaleString();

export const percent = (fraction: number, places = 1) => `${(fraction * 100).toFixed(places)}%`;

export const signedMiles = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)} mi`;

export const speed = (milesPerHour: number) => `${milesPerHour.toFixed(1)} mph`;

export const cadence = (stepsPerMinute: number) => `${Math.round(stepsPerMinute)} spm`;

/// Durations run from minutes to days, so the unit has to adapt rather than
/// showing "12960 minutes".
export function duration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) {
    const whole = Math.floor(hours);
    const rest = Math.round((hours - whole) * 60);
    return rest === 0 ? `${whole}h` : `${whole}h ${rest}m`;
  }
  return `${(hours / 24).toFixed(1)} days`;
}

const dayFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
const mediumFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

/// Dates from the API are plain calendar dates. Parsing them as UTC keeps the
/// browser's timezone from shifting them a day.
function parse(iso: string): Date | null {
  const parts = iso.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!));
}

export const shortDate = (iso: string) => {
  const date = parse(iso);
  return date ? dayFormatter.format(date) : iso;
};

export const mediumDate = (iso: string) => {
  const date = parse(iso);
  return date ? mediumFormatter.format(date) : iso;
};

/// `YYYY-MM-DD` in the browser's own timezone. The server treats a day as a
/// calendar date with no timezone, so the client decides what "today" means -
/// and a walk rolls over at your midnight, not UTC's.
export function todayKey(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/// "At your pace" is a promise about which pace. The server says whether it
/// measured one from today's readings or fell back to the calibration, so the
/// label can be honest about which is being shown.
export const paceLabel = (basis?: string) =>
  basis === 'measured' ? 'At your current pace' : 'At your usual pace';

/// A day with nothing logged has no place in the ranking, and the API says so
/// with rank 0. "0 of 145" would read as dead last rather than as not counted.
export const rank = (todaysRank: number, totalRankedDays: number) =>
  todaysRank === 0 ? 'Not yet ranked' : `${todaysRank} of ${totalRankedDays}`;

/** Just the number of days. An unfinished today is deliberately not counted,
 *  but saying so inside the value needed its own explanation — the plain
 *  number is already the whole truth. */
export function streak(days: number): string {
  if (days <= 0) return 'None';
  return days === 1 ? '1 day' : `${days} days`;
}
